import React, { useState } from "react";
import moment from "moment";
import logger from "../utils/logger";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../shadcn/ui/dialog";
import { Button } from "../shadcn/ui/button";
import { Input } from "../shadcn/ui/input";
import { Label } from "../shadcn/ui/label";
import { Textarea } from "../shadcn/ui/textarea";
import { Badge } from "../shadcn/ui/badge";
import { Loader2 } from "lucide-react";

function AnnouncementFormModal({
  isOpen,
  isClose,
  onSubmit,
  title,
  declineButton,
  acceptButton,
  id,
  initialTitle,
  initialMessage,
  initialSelectedRoles,
  initialUserIds,
  initialDatetimeToSend,
  createdAt,
  readOnly,
  showRoles,
  showUserIds,
  availableRoles,
  selectedUsers,
}) {
  const [formData, setFormData] = useState({
    id,
    title: initialTitle || "",
    message: initialMessage || "",
    selectedRoles: initialSelectedRoles || [],
    userId: initialUserIds || "",
    datetimeToSend: initialDatetimeToSend || new Date(),
  });
  const [loading, setLoading] = useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = e => {
    e.preventDefault();

    // Create the payload first
    const payload = {
      id: formData.id,
      title: formData.title,
      message: formData.message,
      selectedRoles: formData.selectedRoles,
      userId: formData.userId,
      datetimeToSend: formData.datetimeToSend,
    };

    // Only set loading true when actually submitting
    setLoading(true);

    // Call onSubmit and handle the promise
    onSubmit(payload)
      .then(() => {
        isClose();
      })
      .catch(error => {
        logger.error("Error in submit:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const formatDate = date => {
    const momentDate = moment(date);
    const today = moment().startOf("day");
    const tomorrow = moment().add(1, "days").startOf("day");

    if (momentDate.isSame(today, "day")) {
      return `Today, ${momentDate.format("h:mm A")}`;
    } else if (momentDate.isSame(tomorrow, "day")) {
      return `Tomorrow, ${momentDate.format("h:mm A")}`;
    } else {
      return momentDate.format("MMMM Do YYYY, h:mm A");
    }
  };

  const formatDateTime = date => {
    if (!date) return "";
    return moment(date).local().format("MMMM D, YYYY [at] h:mm A");
  };

  return (
    <Dialog open={isOpen} onOpenChange={isClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-center">{title}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input id="title" type="text" value={initialTitle} readOnly={true} className="bg-gray-50" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea id="message" rows={3} value={initialMessage} readOnly={true} className="bg-gray-50" />
          </div>

          {showRoles && initialSelectedRoles?.length > 0 && (
            <div className="space-y-2">
              <Label>Selected Roles</Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-gray-50 min-h-[38px]">
                {initialSelectedRoles.map(roleValue => {
                  const role = availableRoles.find(r => r.value === roleValue);
                  return (
                    <Badge key={roleValue} variant="secondary">
                      {role?.label || roleValue}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {showUserIds && initialUserIds?.length > 0 && (
            <div className="space-y-2">
              <Label>Selected Users</Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-gray-50 min-h-[38px] max-h-[200px] overflow-y-auto">
                {selectedUsers.map(user => (
                  <Badge key={user.id} variant="secondary" className="break-all">
                    {user.firstName} {user.lastname} ({user.email})
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="scheduledTime">Scheduled Time</Label>
            <Input
              id="scheduledTime"
              type="text"
              value={formatDateTime(initialDatetimeToSend)}
              readOnly={true}
              className="bg-gray-50"
            />
          </div>
        </form>

        <DialogFooter className="flex justify-center gap-4">
          <Button type="button" variant="outline" onClick={isClose} className="w-32">
            {declineButton}
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={loading} className="w-32">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {acceptButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AnnouncementFormModal;
